import $ from 'cash-dom'
import { adaptiveDebounce, debounce } from '../utilities/toolkit'
import { proxy } from '../resources/proxy.ts'

const zerobounceProxyEnabled = Pura?.dev?.enable_zerobounce_proxy || false

const validateEmailFormat = (email) => {
  const basicRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const domainRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

  return (
    basicRegex.test(email) &&
    (() => {
      const atIndex = email.lastIndexOf('@')
      if (atIndex === -1) return false
      const domain = email.substring(atIndex + 1)
      return (
        !domain.startsWith('-') &&
        !domain.endsWith('-') &&
        domainRegex.test(domain)
      )
    })()
  )
}

const validateName = (element) => {
  const value = element.value.trim()
  const name = element.getAttribute('name')
  const group = $(element).closest('.input-group')
  const errorField = group.find('.input-group-error')
  const form = group.closest('form')

  // Value is bad, show error message
  if (value.length == 0) {
    group.attr('data-valid', 'false')
    form.trigger('validate-form')
    errorField.html(`${name} must be filled in`)
    return false
  }

  // Value is good, reset fields
  group.attr('data-valid', 'true')
  form.trigger('validate-form')
  setTimeout(() => {
    errorField.html('')
  }, 100)

  return true
}

const validateText = (e) => {
  const value = e.target.value.trim()
  const name = e.target.getAttribute('name')
  const group = $(e.target).closest('.input-group')
  const errorField = group.find('.input-group-error')
  const form = group.closest('form')

  // Value is bad, show error message
  if (value.length == 0) {
    group.attr('data-valid', 'false')
    form.trigger('validate-form')
    errorField.html(`${name} must be filled in`)
    errorField.removeClass('hidden')
    return false
  }

  // Value is good, reset fields
  group.attr('data-valid', 'true')
  form.trigger('validate-form')
  errorField.addClass('hidden')
  setTimeout(() => {
    errorField.html('')
  }, 100)
  return true
}

const displayErrorMessage = (errorField, message) => {
  errorField.html(message)
  errorField.removeClass('hidden')
  errorField.css({
    opacity: 1,
    transition: 'opacity 0.3s ease-in-out',
  })
}

const hideErrorMessage = (errorField) => {
  errorField.addClass('hidden').html('')
  errorField.css({
    opacity: 0,
    transition: 'opacity 0.3s ease-in-out',
  })
}

export const validateEmail = async (input) => {
  const form = $(input).closest('form')
  const formId = form.attr('id')
  const inputGroup = $(input).closest('.input-group')
  const errorField = inputGroup.find('.input-group-error')
  const spinner = inputGroup.find('[data-element="spinner"]')
  const key = 'theme'

  $(input)
    .off('input.validation')
    .on('input.validation', () => {
      const currentForm = $(input).closest('form')
      const currentSubmit = currentForm.find('[type="submit"]')

      hideErrorMessage(errorField)
      inputGroup.attr('data-valid', '')
      inputGroup.removeClass('invalid')
      currentSubmit.prop('disabled', true)

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      const value = $(input).val().trim()
      if (value && !emailRegex.test(value)) {
        inputGroup.addClass('invalid')
      }
    })

  const rawValue = $(input).val().trim()
  const emails = rawValue
    .split(',')
    .map((e) => e.trim())
    .filter((e) => e !== '')

  if (emails.length === 0) {
    hideErrorMessage(errorField)
    inputGroup.attr('data-valid', '')
    inputGroup.removeClass('invalid')
    spinner.addClass('hidden')
    form.trigger('validate-form')
    return
  }

  const invalidFormatEmails = emails.filter(
    (email) => !validateEmailFormat(email)
  )

  if (invalidFormatEmails.length > 0) {
    spinner.addClass('hidden')
    inputGroup.attr('data-valid', 'false')
    inputGroup.addClass('invalid')
    const errorMessage =
      invalidFormatEmails.length === 1
        ? 'Invalid email address'
        : 'One or more email addresses are invalid'
    displayErrorMessage(errorField, errorMessage)
    form.trigger('validate-form')
    return
  }

  spinner.removeClass('hidden')
  inputGroup.attr('data-valid', '')

  if (
    !zerobounceProxyEnabled ||
    formId === 'email-sign-in-form' ||
    formId === 'send-reset-code-form' ||
    formId === 'reset-password-form'
  ) {
    spinner.addClass('hidden')
    inputGroup.attr('data-valid', 'true')
    inputGroup.removeClass('invalid')
    hideErrorMessage(errorField)
    form.trigger('validate-form')
    return
  }

  try {
    const validationPromises = emails.map((email) =>
      proxy('/email-validation', { email, key }, 'GET', null)
    )

    const responses = await Promise.all(validationPromises)
    spinner.addClass('hidden')

    const isInvalidStatus = ['invalid', 'spamtrap']
    const allValid = responses.every((response) => {
      if (
        !response ||
        !response.data ||
        typeof response.data.status === 'undefined'
      ) {
        return false
      }

      const { status } = response.data
      return !isInvalidStatus.includes(status)
    })

    if (allValid) {
      inputGroup.attr('data-valid', 'true')
      inputGroup.removeClass('invalid')
      hideErrorMessage(errorField)
    } else {
      inputGroup.attr('data-valid', 'false')
      inputGroup.addClass('invalid')
      const errorMessage =
        emails.length === 1
          ? 'Invalid email address'
          : 'One or more email addresses are invalid'
      displayErrorMessage(errorField, errorMessage)
    }

    form.trigger('validate-form')
  } catch (error) {
    spinner.addClass('hidden')
    const catchErrorMessage =
      'Something went wrong validating email. Please try again.'
    displayErrorMessage(errorField, catchErrorMessage)
    inputGroup.attr('data-valid', 'false')
    inputGroup.addClass('invalid')
    form.trigger('validate-form')
  }
}

const validateCollabFields = (e) => {
  const value = e.target.value.trim()
  const name = e.target.getAttribute('placeholder')?.toLowerCase()
  const group = $(e.target).closest('.input-group')
  const errorField = group.find('.input-group-error')
  const form = group.closest('form')

  // Value is bad, show error message
  if (value.length == 0) {
    group.attr('data-valid', 'false')
    form.trigger('validate-form')
    errorField.html(`${name} must be filled in`)
    return
  }

  // Value is good, reset fields
  group.attr('data-valid', 'true')
  form.trigger('validate-form')
  setTimeout(() => {
    errorField.html('')
  }, 100)
}

const validateCollabsName = (element) => {
  const value = element.value.trim()
  const name = element.getAttribute('placeholder')?.toLowerCase()
  const group = $(element).closest('.input-group')
  const errorField = group.find('.input-group-error')
  const form = group.closest('form')

  // Value is bad, show error message
  if (value.length == 0) {
    group.attr('data-valid', 'false')
    form.trigger('validate-form')
    errorField.html(`${name} must be filled in`)
    return false
  }

  // Value is good, reset fields
  group.attr('data-valid', 'true')
  form.trigger('validate-form')
  setTimeout(() => {
    errorField.html('')
  }, 100)

  return true
}

const validatePassword = (element) => {
  const value = element.value.trim()
  const group = $(element).closest('.input-group')
  const errorField = group.find('.input-group-error')
  const confirmId = element.getAttribute('data-confirm-password-id')
  const form = group.closest('form')
  const formId = form.attr('id')

  if (confirmId) {
    const confirmField = $(`#${confirmId}`)
    if (confirmField.val().length > 0) {
      $(`#${confirmId}`).trigger('change')
    }
  }

  if (value.length == 0) {
    if (
      group.attr('data-auth-error') === 'true' &&
      formId === 'account-password-form'
    ) {
      form.trigger('validate-form')
      return false
    }
    group.attr('data-valid', '')
    group.removeClass('invalid')
    form.trigger('validate-form')
    errorField.addClass('hidden')
    setTimeout(() => {
      errorField.html('')
    }, 100)
    return false
  }

  // Error, must be 6 chars or more
  if (value.length < 6) {
    group.attr('data-valid', 'false')
    errorField.html('Password must be at least 6 characters long')
    form.trigger('validate-form')
    errorField.removeClass('hidden')
    return false
  }

  // Special validation for complete signup forms - no whitespace allowed
  if (
    formId === 'account-complete-signup-form' ||
    formId === 'account-complete-new-account-form'
  ) {
    const noWhitespaceRegex = /^\S+$/
    if (!noWhitespaceRegex.test(element.value)) {
      group.attr('data-valid', 'false')
      errorField.html('Password cannot contain spaces')
      form.trigger('validate-form')
      errorField.removeClass('hidden')
      return false
    }
  }

  // Value is good, reset fields
  group.attr('data-valid', 'true')
  form.trigger('validate-form')
  errorField.addClass('hidden')

  // Clear the auth error flag once user provides input
  if (group.attr('data-auth-error') === 'true') {
    group.removeAttr('data-auth-error')
  }

  setTimeout(() => {
    errorField.html('')
  }, 100)
  return true
}

const validateConfirmPassword = (element) => {
  const value = element.value.trim()
  const group = $(element).closest('.input-group')
  const errorField = group.find('.input-group-error')
  const originalPassword = $(
    `#${element.getAttribute('data-compare-to')}`
  ).val()
  const form = group.closest('form')

  // If they haven't entered anything yet to compare
  if (originalPassword.length == 0) {
    group.attr('data-valid', '')
    form.trigger('validate-form')
    errorField.html('')
    errorField.addClass('hidden')

    return false
  }

  if (value.length == 0) {
    group.attr('data-valid', 'false')
    form.trigger('validate-form')
    errorField.html('you must confirm your password')
    errorField.removeClass('hidden')

    return false
  }

  if (value != originalPassword) {
    group.attr('data-valid', 'false')
    form.trigger('validate-form')
    errorField.removeClass('hidden')
    errorField.html('passwords must match')

    return false
  }

  // Value is good, reset fields
  group.attr('data-valid', 'true')
  form.trigger('validate-form')
  errorField.addClass('hidden')
  errorField.html('')
  return true
}

const validatePhoneNumber = (element) => {
  const value = element.value.trim()
  const group = $(element).closest('.input-group')
  const errorField = group.find('.input-group-error')
  const form = group.closest('form')

  // Regular expression for phone number validation
  const phoneRegex = /^(\+\d{1,3}[- ]?)?\(?\d{3}\)?[- ]?\d{3}[- ]?\d{4}$/

  // When input is empty, set to empty state instead of invalid
  if (value.length == 0) {
    group.attr('data-valid', '')
    group.removeClass('invalid')
    form.trigger('validate-form')
    errorField.addClass('hidden')
    return false
  }

  if (!phoneRegex.test(value)) {
    group.attr('data-valid', 'false')
    form.trigger('validate-form')
    errorField.html('Please enter a valid mobile phone number.')
    errorField.removeClass('hidden')
    return false
  }

  // Value is good, reset fields
  group.attr('data-valid', 'true')
  form.trigger('validate-form')
  errorField.addClass('hidden')
  setTimeout(() => {
    errorField.html('')
  }, 100)
  return true
}

const validateZipCode = (element) => {
  const value = element.value.trim()
  const group = $(element).closest('.input-group')
  const errorField = group.find('.input-group-error')
  const form = group.closest('form')

  const zipRegex = /^\d{5}(-\d{4})?$/

  if (value.length == 0) {
    group.attr('data-valid', '')
    group.removeClass('invalid')
    form.trigger('validate-form')
    errorField.addClass('hidden')
    return false
  }

  if (!zipRegex.test(value)) {
    group.attr('data-valid', 'false')
    form.trigger('validate-form')
    errorField.html('Please enter a valid zip code.')
    errorField.removeClass('hidden')
    return false
  }

  group.attr('data-valid', 'true')
  form.trigger('validate-form')
  errorField.addClass('hidden')
  setTimeout(() => {
    errorField.html('')
  }, 100)
  return true
}

const validateVerificationCode = (element) => {
  const value = element.value.trim()
  const group = $(element).closest('.input-group')
  const errorField = group.find('.input-group-error')
  const form = group.closest('form')

  if (value.length == 0) {
    group.attr('data-valid', '')
    form.trigger('validate-form')
    errorField.html('')
    return false
  }

  // Error, must be 6 chars or more
  if (value.length < 6) {
    group.attr('data-valid', 'false')
    errorField.html('code must be 6 characters long')
    form.trigger('validate-form')
    return false
  }

  group.attr('data-valid', 'true')
  form.trigger('validate-form')
  errorField.html('')
  return true
}

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
const validateDelay = isMobile ? 1000 : 600
const nameFields = $('[data-validate="name"] [type="text"]')
nameFields.keyup(
  debounce((e) => {
    validateName(e.target)
  }, validateDelay)
)
nameFields.change((e) => {
  validateName(e.target)
})

const textFields = $('[data-validate="text"] [type="text"]')
textFields.keyup(debounce(validateText, validateDelay))
textFields.change(validateText)

const emailFields = $('[data-validate="email"] [type="email"]')

emailFields.on(
  'keyup input change',
  adaptiveDebounce(
    (e) => {
      if (e.target.value.length > 5) {
        validateEmail(e.target)
      }
    },
    300,
    validateDelay
  )
)

const phoneFields = $('[data-validate="phone"] [type="tel"]')

phoneFields.on('blur', (e) => {
  validatePhoneNumber(e.target)
})

phoneFields.on(
  'keyup input change',
  debounce((e) => {
    validatePhoneNumber(e.target)
  }, validateDelay)
)
phoneFields.change((e) => {
  validatePhoneNumber(e.target)
})

const zipCodeFields = $('[data-validate="zip-code"] [type="text"]')

zipCodeFields.on('blur', (e) => {
  validateZipCode(e.target)
})

zipCodeFields.on(
  'keyup input change',
  debounce((e) => {
    validateZipCode(e.target)
  }, validateDelay)
)

const collabFields = $('[data-validate="collab-text"] [type="text"]')
collabFields.keyup(debounce(validateCollabFields, validateDelay))
collabFields.change(validateCollabFields)

const collabNameFields = $('[data-validate="collab-name"] [type="text"]')
collabNameFields.keyup(
  debounce((e) => {
    validateCollabsName(e.target)
  }, validateDelay)
)
collabNameFields.change((e) => {
  validateCollabsName(e.target)
})

const passwordFields = $('[data-validate="password"] [type="password"]')
passwordFields.keyup(
  debounce((e) => {
    validatePassword(e.target)
  }, validateDelay)
)
passwordFields.change((e) => {
  validatePassword(e.target)
})

const confirmPasswordFields = $(
  '[data-validate="confirm-password"] [type="password"]'
)
confirmPasswordFields.keyup(
  debounce((e) => {
    validateConfirmPassword(e.target)
  }, validateDelay)
)
confirmPasswordFields.change((e) => {
  validateConfirmPassword(e.target)
})

const confirmVerificationCodeFields = $(
  '[data-validate="verification-code"] [type="number"]'
)
confirmVerificationCodeFields.keyup(
  debounce((e) => {
    validateVerificationCode(e.target)
  }, validateDelay)
)
confirmVerificationCodeFields.change((e) => {
  validateVerificationCode(e.target)
})

$('#look-up-form').on('validate-form', (e) => {
  const form = $(e.target)
  const submit = form.find('[type="submit"]')
  const validateFields = form.find('.input-group[data-validate]').not('.hidden')

  for (let i = 0; i < validateFields.length; ++i) {
    if (validateFields.eq(i).attr('data-valid') != 'true') {
      submit.prop('disabled', true)
      return
    }
  }
  submit.removeAttr('disabled')
})

let formValidated = false

$('#contact_form, #product-notification-form, #email-capture-form').on(
  'validate-form',
  (e) => {
    const form = $(e.target)
    const submit = form.find('[type="submit"]')
    const validateFields = form.find('[data-validate]').not('.hidden')
    const checkbox = form.find('input[type="checkbox"]').val()

    for (let i = 0; i < validateFields.length; ++i) {
      if (validateFields.eq(i).attr('data-valid') === 'false') {
        formValidated = false
        submit.prop('disabled', true)
        return
      }
    }

    formValidated = true
    if (checkbox == 'true') {
      submit.removeAttr('disabled')
    }
  }
)

// Remove the default handler for forms that need custom validation
$('[data-validate="form"]')
  .not('#account-complete-signup-form, #account-complete-new-account-form')
  .on('validate-form', (e) => {
    const form = $(e.target)
    const submit = form.find('[type="submit"]')
    const validateFields = form.find('[data-validate]').not('.hidden')

    for (let i = 0; i < validateFields.length; ++i) {
      if (validateFields.eq(i).attr('data-valid') != 'true') {
        submit.prop('disabled', true)
        return
      }
    }
    submit.removeAttr('disabled')
  })

// Custom validation for account completion forms including social login
$(
  '#account-complete-signup-form, #account-complete-new-account-form, #create-account-from-social-form'
).on('validate-form', (e) => {
  const form = $(e.target)
  const submit = form.find('[type="submit"]')
  const validateFields = form.find('[data-validate]').not('.hidden')
  const formId = form.attr('id')

  // Get the appropriate terms checkbox based on form
  let termsCheckbox
  if (formId === 'account-complete-signup-form') {
    termsCheckbox = form.find('#complete-signup-terms')
  } else if (formId === 'account-complete-new-account-form') {
    termsCheckbox = form.find('#complete-new-account-terms')
  } else if (formId === 'create-account-from-social-form') {
    termsCheckbox = form.find('#create-social-terms')
  }

  // Check if all fields are valid
  let allFieldsValid = true
  for (let i = 0; i < validateFields.length; ++i) {
    if (validateFields.eq(i).attr('data-valid') !== 'true') {
      allFieldsValid = false
      break
    }
  }

  // Enable button only if all fields are valid AND terms checkbox is checked
  if (allFieldsValid && termsCheckbox && termsCheckbox.is(':checked')) {
    submit.removeAttr('disabled')
  } else {
    submit.prop('disabled', true)
  }
})

// Listen for terms checkbox changes on all account forms
$(document).on(
  'change',
  '#complete-signup-terms, #complete-new-account-terms, #create-social-terms',
  (e) => {
    const checkbox = $(e.target)
    const form = checkbox.closest('form')

    // Trigger form validation to update button state
    form.trigger('validate-form')
  }
)
$('#newsletter-footer, #newsletter-popup').on('validate-form', (e) => {
  const form = $(e.target)
  const submit = form.find('[type="submit"]')
  const email = form.find('[data-validate="email"]').attr('data-valid')
  const phone = form.find('[data-validate="phone"]').attr('data-valid')
  const checkbox = form.find('input[type="checkbox"]').val()

  if (email === 'true' || phone === 'true') {
    formValidated = true
  } else {
    formValidated = false
  }

  if (checkbox == 'true' && formValidated) {
    submit.removeAttr('disabled')
    return
  }
  submit.prop('disabled', true)
})
$('#newsletter-footer, #newsletter-popup').on('submit', (e) => {
  e.preventDefault()
  const form = $(e.target)
  const email = form.find('[data-validate="email"]').val()
  window.ElevarDataLayer.push({
    event: 'dl_subscribe',
    lead_type: 'email',
    user_properties: { customer_email: email },
  })
  setTimeout(() => form[0].submit(), 50)
})
$('input[readOnly]').on('click', (e) => {
  const readOnly = $(e.target)
  const form = readOnly.closest('form')
  const errorMessage = $(form).find('.form-group-error')

  if (errorMessage) {
    errorMessage.removeClass('hidden')
  }
})

$('.input-group-checkbox input[type="checkbox"]').change((e) => {
  const checkbox = $(e.target)
  const form = checkbox.closest('form')
  const email = $(form).find('[data-validate="email"]')
  const phone = $(form).find('[data-validate="phone"]')
  const submit = form.find('[type="submit"]')

  formValidated =
    email.attr('data-valid') === 'true' || phone.attr('data-valid') === 'true'

  if (checkbox.is(':checked')) {
    checkbox.attr('value', 'true')
    if (formValidated) submit.removeAttr('disabled')
  } else {
    checkbox.attr('value', 'false')
    submit.prop('disabled', true)
  }
})
